import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'illustration');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
    window.scrollTo(0, 0);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 萝莉次元` : '分类页面',
    description: currentCategory?.description || '',
    url: `https://lolidimension.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '萝莉次元',
      url: 'https://lolidimension.com',
    },
  };

  return (
    <>
      <JsonLd data={collectionPageSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory?.name || '' },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className={`${currentCategory?.icon || 'ri-folder-line'} text-xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                {currentCategory?.name || '分类'}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                {currentCategory?.description || ''}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Articles List */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-14">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.author}</span>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容</p>
            </div>
          )}
        </section>

        <Footer />
      </div>
    </>
  );
}